/**
 * Admin Welcome Page: edit welcome landing page content (hero, about, mission).
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../../../components/layout/index.ts";
import {
  IconDove,
  IconAlertTriangle,
  IconCheck,
  IconPhoto,
  IconPlay,
} from "../../../components/Icons.tsx";
import { getTelegram, haptic } from "../../../lib/telegram.ts";
import { useAuth } from "../../../lib/auth.tsx";
import {
  graphql,
  getErrorMessage,
  withMinDelay,
  getAuthHeaders,
  ADMIN_CHURCH_INFO_QUERY,
  UPDATE_CHURCH_INFO_MUTATION,
} from "../../../lib/graphql.ts";
import { config } from "../../../config.ts";
import { safeParseResponseJson } from "../../../lib/json-utils.ts";

interface WelcomeData {
  id: string;
  welcomeHeroVideoUrl: string | null;
  welcomeHeroImageUrl: string | null;
  welcomeHeroTitle: string | null;
  welcomeHeroSubtitle: string | null;
  welcomeAboutTitle: string | null;
  welcomeAboutText: string | null;
  welcomeDaysCount: number | null;
  welcomeMissionText: string | null;
}

export function AdminWelcomePage() {
  const navigate = useNavigate();
  const tg = getTelegram();
  const { account } = useAuth();
  const hasLoadedOnce = useRef(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageCacheBust, setImageCacheBust] = useState(0);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const [heroVideoUrl, setHeroVideoUrl] = useState("");
  const [heroImageUrl, setHeroImageUrl] = useState("");
  const [heroTitle, setHeroTitle] = useState("");
  const [heroSubtitle, setHeroSubtitle] = useState("");
  const [aboutTitle, setAboutTitle] = useState("");
  const [aboutText, setAboutText] = useState("");
  const [daysCount, setDaysCount] = useState<number | "">("");
  const [missionText, setMissionText] = useState("");

  const loadData = useCallback(async () => {
    if (!account) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await withMinDelay(
        graphql<{ adminChurchInfo: WelcomeData | null }>(ADMIN_CHURCH_INFO_QUERY)
      );
      const raw = data.adminChurchInfo;
      if (raw) {
        setHeroVideoUrl(raw.welcomeHeroVideoUrl ?? "");
        setHeroImageUrl(raw.welcomeHeroImageUrl ?? "");
        setHeroTitle(raw.welcomeHeroTitle ?? "");
        setHeroSubtitle(raw.welcomeHeroSubtitle ?? "");
        setAboutTitle(raw.welcomeAboutTitle ?? "");
        setAboutText(raw.welcomeAboutText ?? "");
        setDaysCount(raw.welcomeDaysCount ?? "");
        setMissionText(raw.welcomeMissionText ?? "");
      } else {
        setHeroVideoUrl("");
        setHeroImageUrl("");
        setHeroTitle("Добро пожаловать");
        setHeroSubtitle("Первая русскоязычная Евангельская Церковь в Таиланде");
        setAboutTitle("Кто мы?");
        setAboutText("");
        setDaysCount(700);
        setMissionText("");
      }
      hasLoadedOnce.current = true;
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [account]);

  useEffect(() => {
    if (account) loadData();
  }, [account, loadData]);

  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/admin/content");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  const handleSave = async () => {
    haptic("light");
    setIsSaving(true);
    setError(null);
    try {
      await graphql<{ updateChurchInfo: WelcomeData }>(
        UPDATE_CHURCH_INFO_MUTATION,
        {
          input: {
            welcomeHeroVideoUrl: heroVideoUrl.trim() || null,
            welcomeHeroImageUrl: heroImageUrl.trim() || null,
            welcomeHeroTitle: heroTitle.trim() || null,
            welcomeHeroSubtitle: heroSubtitle.trim() || null,
            welcomeAboutTitle: aboutTitle.trim() || null,
            welcomeAboutText: aboutText.trim() || null,
            welcomeDaysCount: daysCount === "" ? null : Number(daysCount),
            welcomeMissionText: missionText.trim() || null,
          },
        }
      );
      await loadData();
      haptic("success");
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageUpload = async (file: File) => {
    setIsUploadingImage(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch(
        `${config.apiUrl}/upload/content/welcome-image`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: formData,
        }
      );
      if (!response.ok) {
        const data = await safeParseResponseJson<{ error?: string }>(
          response,
          "welcome image upload"
        );
        throw new Error(data.error || "Ошибка загрузки");
      }
      const result = (await response.json()) as { url: string };
      setHeroImageUrl(result.url);
      setImageCacheBust(Date.now());
      haptic("success");
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };

  const handleVideoUpload = async (file: File) => {
    setIsUploadingVideo(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch(
        `${config.apiUrl}/upload/content/welcome-video`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: formData,
        }
      );
      if (!response.ok) {
        const data = await safeParseResponseJson<{ error?: string }>(
          response,
          "welcome video upload"
        );
        throw new Error(data.error || "Ошибка загрузки");
      }
      const result = (await response.json()) as { url: string };
      setHeroVideoUrl(result.url);
      haptic("success");
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsUploadingVideo(false);
      if (videoInputRef.current) videoInputRef.current.value = "";
    }
  };

  const fieldClass =
    "w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500";
  const labelClass =
    "block text-[12px] font-medium text-slate-400 uppercase tracking-wide mb-1";

  return (
    <Layout>
      <div className="pb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center text-teal-600">
            <IconDove className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-slate-900">Welcome Page</h1>
            <p className="text-[13px] text-slate-500">
              Посадочная страница /welcome
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 flex items-center gap-2 text-amber-600 text-sm">
            <IconAlertTriangle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="card p-8 text-center text-slate-500 text-sm">
            Загрузка…
          </div>
        ) : (
          <div className="space-y-4">
            {/* Hero Section */}
            <div className="card overflow-hidden p-4">
              <h2 className="text-base font-semibold text-slate-900 mb-4">
                Hero-секция
              </h2>

              {/* Video */}
              <div className="mb-4">
                <label className={labelClass}>Видео (MP4, WebM, MOV)</label>
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleVideoUpload(f);
                  }}
                />
                {heroVideoUrl && !isUploadingVideo ? (
                  <div className="relative rounded-xl overflow-hidden bg-slate-900 mb-2">
                    <video
                      src={heroVideoUrl}
                      className="w-full aspect-video object-cover"
                      muted
                      loop
                      playsInline
                      controls
                    />
                  </div>
                ) : null}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => videoInputRef.current?.click()}
                    disabled={isUploadingVideo}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-700 active:bg-slate-200 disabled:opacity-50"
                  >
                    <IconPlay className="w-4 h-4" />
                    {isUploadingVideo
                      ? "Загрузка…"
                      : heroVideoUrl
                        ? "Заменить видео"
                        : "Загрузить видео"}
                  </button>
                  {heroVideoUrl && !isUploadingVideo && (
                    <button
                      type="button"
                      onClick={() => setHeroVideoUrl("")}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-amber-700 bg-amber-50 active:bg-amber-100"
                    >
                      Удалить
                    </button>
                  )}
                </div>
              </div>

              {/* Fallback Image */}
              <div className="mb-4">
                <label className={labelClass}>
                  Fallback-картинка (если нет видео)
                </label>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleImageUpload(f);
                  }}
                />
                {heroImageUrl && !isUploadingImage ? (
                  <div className="relative rounded-xl overflow-hidden bg-slate-100 mb-2">
                    <img
                      src={
                        heroImageUrl +
                        (heroImageUrl.includes("?") ? "&" : "?") +
                        `_t=${imageCacheBust}`
                      }
                      alt=""
                      className="w-full aspect-video object-cover"
                    />
                  </div>
                ) : null}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => imageInputRef.current?.click()}
                    disabled={isUploadingImage}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-700 active:bg-slate-200 disabled:opacity-50"
                  >
                    <IconPhoto className="w-4 h-4" />
                    {isUploadingImage
                      ? "Загрузка…"
                      : heroImageUrl
                        ? "Заменить"
                        : "Загрузить"}
                  </button>
                  {heroImageUrl && !isUploadingImage && (
                    <button
                      type="button"
                      onClick={() => setHeroImageUrl("")}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-amber-700 bg-amber-50 active:bg-amber-100"
                    >
                      Удалить
                    </button>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <label className={labelClass}>Заголовок</label>
                <input
                  type="text"
                  value={heroTitle}
                  onChange={(e) => setHeroTitle(e.target.value)}
                  placeholder="Добро пожаловать"
                  className={fieldClass}
                />
              </div>
              <div>
                <label className={labelClass}>Подзаголовок</label>
                <textarea
                  value={heroSubtitle}
                  onChange={(e) => setHeroSubtitle(e.target.value)}
                  rows={2}
                  placeholder="Краткое описание..."
                  className={`${fieldClass} resize-none`}
                />
              </div>
            </div>

            {/* About Section */}
            <div className="card overflow-hidden p-4">
              <h2 className="text-base font-semibold text-slate-900 mb-4">
                Секция «О нас»
              </h2>
              <div className="mb-4">
                <label className={labelClass}>Заголовок</label>
                <input
                  type="text"
                  value={aboutTitle}
                  onChange={(e) => setAboutTitle(e.target.value)}
                  placeholder="Кто мы?"
                  className={fieldClass}
                />
              </div>
              <div className="mb-4">
                <label className={labelClass}>Текст</label>
                <textarea
                  value={aboutText}
                  onChange={(e) => setAboutText(e.target.value)}
                  rows={5}
                  placeholder="Краткое описание церкви..."
                  className={`${fieldClass} resize-none`}
                />
              </div>
              <div>
                <label className={labelClass}>Счётчик дней</label>
                <input
                  type="number"
                  value={daysCount}
                  onChange={(e) =>
                    setDaysCount(e.target.value === "" ? "" : Number(e.target.value))
                  }
                  placeholder="700"
                  min={0}
                  className={fieldClass}
                />
                <p className="mt-1 text-[11px] text-slate-400">
                  Отображается как «700+ дней в молитве»
                </p>
              </div>
            </div>

            {/* Mission Section */}
            <div className="card overflow-hidden p-4">
              <h2 className="text-base font-semibold text-slate-900 mb-4">
                Миссия церкви
              </h2>
              <div>
                <label className={labelClass}>Текст миссии</label>
                <textarea
                  value={missionText}
                  onChange={(e) => setMissionText(e.target.value)}
                  rows={4}
                  placeholder="Помогать людям узнавать Бога..."
                  className={`${fieldClass} resize-none`}
                />
              </div>
            </div>

            {/* Save Button */}
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {isSaving ? (
                "Сохранение…"
              ) : (
                <>
                  <IconCheck className="w-4 h-4" />
                  Сохранить
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}
